import { useState, useEffect, useRef, type MouseEvent } from 'react';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { api, eventShareApi } from '../../api/client';
import type { CarouselEvent, EventsResponse, SharedEventGroup } from '../../types';
import { ShareEventModal } from '../../components/ShareEventModal';
import { format } from 'date-fns';
import { parseEventDate } from '../../utils/eventDate';
import { getEventTemplate, EVENT_TEMPLATES } from '../../utils/eventTemplates';
import { EventLink } from '../../components/EventLink';

type ListTab = 'mine' | 'shared';
type ListSort = 'date' | 'member' | 'location' | 'type';
type ListViewMode = 'grid' | 'timeline';

// Saved into this list's own history entry just before opening an event, so
// the event page's Back (history -1) lands on the same tab/sort/view/scroll
// position instead of a fresh "My Events, by date, top of page".
interface ListViewState {
  listView: { tab: ListTab; sort: ListSort; viewMode: ListViewMode; scrollY: number };
}

export function EventsList() {
  const location = useLocation();
  const restored = (location.state as Partial<ListViewState> | null)?.listView;
  const [searchParams] = useSearchParams();
  // Access-grant emails link to ?tab=shared&owner=<accountId> — open on Shared
  // with Me and scroll to that person's group (see sharedWithMeUrl in email.ts).
  const [tab, setTab] = useState<ListTab>(restored?.tab ?? (searchParams.get('tab') === 'shared' ? 'shared' : 'mine'));
  const pendingOwnerScroll = useRef(restored ? null : searchParams.get('owner'));
  const [response, setResponse] = useState<EventsResponse | null>(null);
  const [sharedGroups, setSharedGroups] = useState<SharedEventGroup[]>([]);
  const [sort, setSort] = useState<ListSort>(restored?.sort ?? 'date');
  const [viewMode, setViewMode] = useState<ListViewMode>(restored?.viewMode ?? 'grid');
  const [loading, setLoading] = useState(true);
  const [shareModalEventId, setShareModalEventId] = useState<string | null>(null);
  const [shareModalTitle, setShareModalTitle] = useState('');
  const pendingScrollY = useRef(restored?.scrollY ?? null);
  const navigate = useNavigate();
  const filterWhen = searchParams.get('when');
  const filterWhere = searchParams.get('where');
  const filterWho = searchParams.get('who');
  const filterType = searchParams.get('type');

  useEffect(() => {
    setLoading(true);
    if (tab === 'shared') {
      eventShareApi.getSharedWithMe()
        .then(({ groups }) => setSharedGroups(groups))
        .finally(() => setLoading(false));
    } else {
      api.get(`/events?sort=${sort}&allPhotos=true`)
        .then(({ data }) => setResponse(data))
        .finally(() => setLoading(false));
    }
  }, [sort, tab, location.key]);

  // Restore the scroll position once the restored list has actually rendered.
  useEffect(() => {
    if (loading || pendingScrollY.current === null) return;
    const y = pendingScrollY.current;
    pendingScrollY.current = null;
    requestAnimationFrame(() => window.scrollTo(0, y));
  }, [loading]);

  useEffect(() => {
    if (loading || tab !== 'shared' || !pendingOwnerScroll.current) return;
    const el = document.getElementById(`shared-${pendingOwnerScroll.current}`);
    pendingOwnerScroll.current = null;
    if (el) requestAnimationFrame(() => el.scrollIntoView({ block: 'start' }));
  }, [loading, tab]);

  function saveListView(e: MouseEvent) {
    if (!(e.target as Element).closest('[data-event-nav-id]')) return;
    const state: ListViewState = { listView: { tab, sort, viewMode, scrollY: window.scrollY } };
    navigate(`${location.pathname}${location.search}`, { replace: true, state });
  }

  const events: CarouselEvent[] = response?.events || [];

  const filteredEvents = events.filter(e => {
    if (filterWhen) {
      const year = e.eventDate ? parseEventDate(e.eventDate).getFullYear() : 0;
      if (String(year) !== filterWhen) return false;
    }
    if (filterWhere && e.location !== filterWhere) return false;
    // comma-separated for the sidebar's grouped "Former members" row
    if (filterWho && !e.participants?.some(p => filterWho.split(',').includes(p.userId))) return false;
    if (filterType && getEventTemplate(e.templateKind).kind !== filterType) return false;
    return true;
  });
  const activeFilterLabel = filterWhen
    ? `Year: ${filterWhen}`
    : filterWhere
    ? `Place: ${filterWhere}`
    : filterWho
    ? 'Person filter active'
    : filterType
    ? `Type: ${getEventTemplate(filterType).label}`
    : null;

  const uniquePlaces = new Set(events.map(e => e.location).filter(Boolean)).size;
  const uniquePeople = new Set(events.flatMap(e => e.participants?.map(p => p.userId) ?? [])).size;

  return (
    // Capture phase: runs before an EventLink's own click navigates away.
    <div onClickCapture={saveListView}>
      {/* Tab switcher */}
      <div className="flex gap-1 bg-white border border-indigo-100 rounded-xl p-1 mb-6 w-full sm:w-auto sm:inline-flex">
        <button
          onClick={() => setTab('mine')}
          className={`flex-1 sm:flex-none px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'mine' ? 'bg-indigo-50 text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
        >
          My Events
        </button>
        <button
          onClick={() => setTab('shared')}
          className={`flex-1 sm:flex-none px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'shared' ? 'bg-indigo-50 text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Shared with Me
        </button>
      </div>

      {tab === 'shared' ? (
        loading ? (
          <p className="text-slate-400 text-sm">Loading…</p>
        ) : sharedGroups.length === 0 ? (
          <div className="text-center py-12 lg:py-20">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-slate-500 font-medium">Nothing shared with you yet</p>
            <p className="text-slate-400 text-sm mt-1">When friends share events with you, they'll appear here.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {sharedGroups.map(group => (
              <div key={group.accountId} id={`shared-${group.accountId}`} style={{ scrollMarginTop: 'calc(62px + env(safe-area-inset-top, 0px) + 16px)' }} data-event-nav-scope>
                <SectionHeader label={`${group.owner.name}'s memories`} firstEventId={group.events[0]?.id} sharedOwnerAccountId={group.accountId} playAllCount={group.events.length} />
                <PolaroidGrid events={group.events} readonly />
              </div>
            ))}
          </div>
        )
      ) : (
        <>
          {/* My Events header */}
          <div className="mb-6 lg:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h1 className="text-xl lg:text-2xl font-bold tracking-tight text-slate-900">My Events</h1>
                {events.length > 0 && (
                  <p className="text-sm text-slate-400 mt-1">
                    {filteredEvents.length} {filteredEvents.length === 1 ? 'memory' : 'memories'}
                    {uniquePlaces > 0 && <> &middot; {uniquePlaces} {uniquePlaces === 1 ? 'place' : 'places'}</>}
                    {uniquePeople > 0 && <> &middot; {uniquePeople} {uniquePeople === 1 ? 'person' : 'people'}</>}
                  </p>
                )}
                {activeFilterLabel && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">
                      {activeFilterLabel}
                      <button onClick={() => navigate('/events')} className="hover:text-indigo-900">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      </button>
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:flex-shrink-0">
                <Link
                  to="/events/milestones"
                  className="px-3 lg:px-4 py-2 bg-white border border-dashed border-indigo-300 text-indigo-600 rounded-xl text-sm font-semibold hover:bg-indigo-50 transition-colors"
                >
                  ✨ Milestone Slideshow
                </Link>
                <Link
                  to="/events/collages"
                  className="px-3 lg:px-4 py-2 bg-white border border-dashed border-indigo-300 text-indigo-600 rounded-xl text-sm font-semibold hover:bg-indigo-50 transition-colors"
                >
                  🖼️ Collages
                </Link>
                <Link
                  to="/events/bulk-import"
                  className="px-3 lg:px-4 py-2 bg-white border border-dashed border-indigo-300 text-indigo-600 rounded-xl text-sm font-semibold hover:bg-indigo-50 transition-colors"
                >
                  Bulk Import
                </Link>
                <Link
                  to="/events/new"
                  className="px-3 lg:px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold shadow-md shadow-indigo-200 transition-colors"
                >
                  + Add Event
                </Link>
              </div>
            </div>
            {/* Sort + view controls */}
            <div className="flex gap-2 flex-wrap">
              <div className="flex bg-white border border-indigo-100 rounded-xl p-1 gap-1 w-full overflow-x-auto sm:w-auto sm:inline-flex sm:overflow-visible">
                {(['date', 'member', 'location', 'type'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => { setSort(s); setViewMode('grid'); }}
                    className={`flex-shrink-0 sm:flex-none px-2.5 lg:px-3.5 py-1.5 rounded-lg text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                      sort === s && viewMode === 'grid' ? 'bg-indigo-50 text-indigo-600' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    By {s === 'member' ? 'Person' : s === 'location' ? 'Place' : s === 'type' ? 'Type' : 'Date'}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setViewMode(v => v === 'timeline' ? 'grid' : 'timeline')}
                title="Timeline view"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-colors ${
                  viewMode === 'timeline' ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-indigo-100 text-slate-500 hover:text-slate-700'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                </svg>
                Timeline
              </button>
            </div>
          </div>

          <div data-event-nav-scope>
          {loading ? (
            <p className="text-slate-400 text-sm">Loading…</p>
          ) : viewMode === 'timeline' ? (
            <TimelineView events={filteredEvents} onShare={(id: string, title: string) => { setShareModalEventId(id); setShareModalTitle(title); }} />
          ) : sort === 'member' && response?.groups ? (
            <div className="space-y-10">
              {response.groups.map(group => {
                const groupFiltered = (filterWho
                  ? group.events.filter(e => e.participants?.some(p => p.userId === filterWho))
                  : group.events
                ).slice().sort((a, b) => {
                  const da = a.eventDate ? parseEventDate(a.eventDate).getTime() : 0;
                  const db = b.eventDate ? parseEventDate(b.eventDate).getTime() : 0;
                  return db - da;
                });
                if (groupFiltered.length === 0) return null;
                return (
                  <div key={group.member.id} data-event-nav-scope>
                    <SectionHeader label={group.member.name} firstEventId={groupFiltered[0].id} mode="member" />
                    <PolaroidGrid events={groupFiltered} onShare={(id: string, title: string) => { setShareModalEventId(id); setShareModalTitle(title); }} />
                  </div>
                );
              })}
            </div>
          ) : sort === 'location' && response?.events ? (
            <LocationGrouped events={filteredEvents} onShare={(id: string, title: string) => { setShareModalEventId(id); setShareModalTitle(title); }} />
          ) : sort === 'type' && response?.events ? (
            <TypeGrouped events={filteredEvents} onShare={(id: string, title: string) => { setShareModalEventId(id); setShareModalTitle(title); }} />
          ) : (
            <MasonryByYear events={filteredEvents} onShare={(id: string, title: string) => { setShareModalEventId(id); setShareModalTitle(title); }} />
          )}
          </div>
        </>
      )}

      {shareModalEventId && (
        <ShareEventModal
          eventId={shareModalEventId}
          eventTitle={shareModalTitle}
          onClose={() => setShareModalEventId(null)}
        />
      )}
    </div>
  );
}

// ─── Section header with optional slideshow button ───────────────────────────

// playAllCount swaps the small far-right "Slideshow" pill for a prominent
// "Play all N" button right beside the label — used for Shared with Me, where
// the slideshow is the main way to browse someone's memories end to end and
// the far-right pill was easy to miss (or scrolled off) next to a wide row.
// Shared slideshows are always scoped to ONE person's memories (&owner=) so
// the viewer never mixes in events someone else shared.
// Every play button starts playing (&autoplay=1) rather than opening paused.
function slideshowHref(startAtId: string, opts: { mode?: string; sharedOwnerAccountId?: string }): string {
  if (opts.sharedOwnerAccountId) return `/carousel?startAt=${startAtId}&shared=1&owner=${encodeURIComponent(opts.sharedOwnerAccountId)}&autoplay=1`;
  return `/carousel?startAt=${startAtId}${opts.mode ? `&mode=${opts.mode}` : ''}&autoplay=1`;
}

function SectionHeader({ label, firstEventId, mode, sharedOwnerAccountId, playAllCount }: { label: string; firstEventId?: string; mode?: string; sharedOwnerAccountId?: string; playAllCount?: number }) {
  const navigate = useNavigate();
  const slideshowUrl = firstEventId && slideshowHref(firstEventId, { mode, sharedOwnerAccountId });
  if (slideshowUrl && playAllCount) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-5">
        <span className="text-xs font-bold text-indigo-500 uppercase tracking-widest">{label}</span>
        <button
          onClick={() => navigate(slideshowUrl)}
          className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-sm shadow-indigo-200 transition-colors"
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
          Play all {playAllCount} {playAllCount === 1 ? 'memory' : 'memories'}
        </button>
        <hr className="flex-1 border-indigo-100 min-w-[2rem]" />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 mb-5">
      <span className="text-xs font-bold text-indigo-500 uppercase tracking-widest">{label}</span>
      <hr className="flex-1 border-indigo-100" />
      {firstEventId && (
        <button
          onClick={() => navigate(slideshowHref(firstEventId, { mode, sharedOwnerAccountId }))}
          className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 hover:bg-indigo-600 text-indigo-500 hover:text-white text-xs font-medium transition-colors"
        >
          <svg className="w-3 h-3 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
          Slideshow
        </button>
      )}
    </div>
  );
}

type ShareHandler = (id: string, title: string) => void;

// ─── Year-grouped polaroid grid (default "By Date" view) ─────────────────────

function MasonryByYear({ events, onShare }: { events: CarouselEvent[]; onShare?: ShareHandler }) {
  if (!events.length) {
    return (
      <div className="text-center py-12 lg:py-20">
        <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="text-slate-500 font-medium">No memories yet</p>
        <p className="text-slate-400 text-sm mt-1">Add your first event to get started</p>
        <Link to="/events/new" className="inline-block mt-5 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200">
          + Add Event
        </Link>
      </div>
    );
  }

  const byYear: Record<number, CarouselEvent[]> = {};
  for (const e of events) {
    const year = e.eventDate ? parseEventDate(e.eventDate).getFullYear() : 0;
    if (!byYear[year]) byYear[year] = [];
    byYear[year].push(e);
  }
  for (const year of Object.keys(byYear)) {
    byYear[Number(year)].sort((a, b) => {
      const da = a.eventDate ? parseEventDate(a.eventDate).getTime() : 0;
      const db = b.eventDate ? parseEventDate(b.eventDate).getTime() : 0;
      return db - da;
    });
  }
  const years = Object.keys(byYear).map(Number).sort((a, b) => b - a);
  const isFirst = (year: number) => year === years[0];

  return (
    <div>
      {years.map(year => (
        <section key={year} className="mb-10">
          <SectionHeader label={String(year || 'Unknown Year')} firstEventId={byYear[year][0].id} />
          <PolaroidGrid events={byYear[year]} showAddCard={isFirst(year)} onShare={onShare} />
        </section>
      ))}
    </div>
  );
}

// ─── Polaroid grid ────────────────────────────────────────────────────────────

function PolaroidGrid({ events, showAddCard, onShare, readonly }: {
  events: CarouselEvent[];
  showAddCard?: boolean;
  onShare?: ShareHandler;
  readonly?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 lg:gap-5">
      {events.map(event => <PolaroidCard key={event.id} event={event} onShare={readonly ? undefined : onShare} shared={readonly} />)}
      {showAddCard && <AddPolaroidCard />}
    </div>
  );
}

// ─── Template badge ───────────────────────────────────────────────────────────
// Shown only for a real, non-general templateKind — null/'general' (including
// every pre-existing event, which has no templateKind at all) renders nothing.

function TemplateBadge({ templateKind, className }: { templateKind: string | null; className: string }) {
  if (!templateKind || templateKind === 'general') return null;
  return (
    <span className={className} title={getEventTemplate(templateKind).label} aria-hidden="true">
      {getEventTemplate(templateKind).icon}
    </span>
  );
}

// ─── Play overlay button ──────────────────────────────────────────────────────

function PlayOverlay({ eventId, sharedOwnerAccountId }: { eventId: string; sharedOwnerAccountId?: string }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={e => { e.preventDefault(); e.stopPropagation(); navigate(slideshowHref(eventId, { sharedOwnerAccountId })); }}
      title="Play slideshow"
      // Always visible on touch screens (phones, tablets, the iOS/Android apps),
      // which can't hover; hover-reveal only with a real mouse. pointer:fine too,
      // because some Android WebViews wrongly report hover:hover on touch screens.
      className="absolute top-3.5 right-3.5 z-10 w-8 h-8 rounded-full bg-black/40 hover:bg-indigo-600 text-white flex items-center justify-center opacity-100 [@media(hover:hover)_and_(pointer:fine)]:opacity-0 [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100 transition-all backdrop-blur-sm"
    >
      <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M8 5v14l11-7z"/>
      </svg>
    </button>
  );
}

// ─── Polaroid card ────────────────────────────────────────────────────────────

function PolaroidCard({ event, onShare, shared }: { event: CarouselEvent; onShare?: ShareHandler; shared?: boolean }) {
  const photo = event.photos?.at(-1);
  const dateStr = event.eventDate ? format(parseEventDate(event.eventDate), 'MMM d, yyyy') : null;

  return (
    <div className="relative group bg-white shadow-md hover:shadow-xl hover:-translate-y-1.5 transition-all duration-200 rounded-sm" style={{ padding: '8px 8px 0 8px' }}>
      <EventLink eventId={event.id} title={event.title} className="block">
        <div className="aspect-square overflow-hidden bg-slate-100 relative">
          <TemplateBadge
            templateKind={event.templateKind}
            className="absolute top-1.5 left-1.5 z-10 w-6 h-6 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center text-sm shadow-sm"
          />
          {photo ? (
            <img
              src={photo.filePath}
              alt={event.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              style={{ objectPosition: '50% 25%' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-50 to-slate-100">
              <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>
        <div className="px-1 pt-2.5 pb-3">
          <p className="text-sm font-semibold text-slate-800 truncate leading-snug">{event.title}</p>
          {dateStr && <p className="text-xs text-slate-400 mt-0.5">{dateStr}</p>}
          {event.location && <p className="text-xs text-slate-400 truncate">{event.location}</p>}
          {(event.photos?.length > 0 || event.audioClips?.length > 0 || event.hasOpenContributionRequest) && (
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              <CardPills event={event} />
              {event.hasOpenContributionRequest && (
                <span className="text-[0.65rem] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                  Help requested
                </span>
              )}
            </div>
          )}
        </div>
      </EventLink>
      <PlayOverlay eventId={event.id} sharedOwnerAccountId={shared ? event.accountId : undefined} />
      {onShare && (
        <button
          onClick={e => { e.preventDefault(); e.stopPropagation(); onShare(event.id, event.title); }}
          title="Share event"
          className="absolute bottom-3 right-2 z-10 w-7 h-7 rounded-full bg-white/80 hover:bg-indigo-600 text-slate-400 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-sm"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
          </svg>
        </button>
      )}
    </div>
  );
}

function AddPolaroidCard() {
  return (
    <Link
      to="/events/new"
      className="group bg-white shadow-md hover:shadow-xl hover:-translate-y-1.5 transition-all duration-200 rounded-sm"
      style={{ padding: '8px 8px 0 8px' }}
    >
      <div className="aspect-square border-2 border-dashed border-indigo-100 group-hover:border-indigo-400 group-hover:bg-indigo-50 transition-all flex flex-col items-center justify-center">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 group-hover:bg-indigo-600 flex items-center justify-center mb-2 transition-colors">
          <svg className="w-5 h-5 text-indigo-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </div>
        <p className="text-xs font-semibold text-slate-500 group-hover:text-indigo-600 transition-colors">Add Event</p>
      </div>
      <div className="px-1 pt-2.5 pb-3">
        <p className="text-sm font-semibold text-slate-400">New memory</p>
        <p className="text-xs text-slate-300">Photos, voices & stories</p>
      </div>
    </Link>
  );
}

// ─── Shared pill badges ───────────────────────────────────────────────────────

function CardPills({ event }: { event: CarouselEvent }) {
  return (
    <>
      {(event.photos?.length ?? 0) > 0 && (
        <span className="text-[0.65rem] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
          {event.photos.length} photo{event.photos.length !== 1 ? 's' : ''}
        </span>
      )}
      {event.audioClips?.length > 0 && (
        <span className="text-[0.65rem] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
          ♪ {event.audioClips.length}
        </span>
      )}
    </>
  );
}

// ─── Timeline view ────────────────────────────────────────────────────────────

function TimelineView({ events, onShare }: { events: CarouselEvent[]; onShare?: ShareHandler }) {
  if (!events.length) return (
    <div className="text-center py-12">
      <p className="text-slate-400 text-sm">No memories yet.</p>
    </div>
  );

  const sorted = [...events].sort((a, b) => {
    const da = a.eventDate ? new Date(a.eventDate).getTime() : 0;
    const db = b.eventDate ? new Date(b.eventDate).getTime() : 0;
    return db - da;
  });

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-indigo-100" />
      <div className="space-y-6">
        {sorted.map(event => {
          const photo = event.photos?.at(-1);
          const dateStr = event.eventDate ? format(parseEventDate(event.eventDate), 'MMM d, yyyy') : 'Unknown date';
          return (
            <div key={event.id} className="flex gap-4 pl-6 relative">
              {/* Dot */}
              <div className="absolute left-0 top-3 w-3.5 h-3.5 rounded-full border-2 border-indigo-400 bg-white z-10" />
              <EventLink
                eventId={event.id} title={event.title}
                className="flex gap-3 bg-white rounded-xl shadow-sm border border-slate-100 p-3 flex-1 hover:shadow-md hover:border-indigo-100 transition-all group"
              >
                {photo && (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                    <img src={photo.filePath} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" style={{ objectPosition: '50% 25%' }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-indigo-500 font-medium mb-0.5">{dateStr}</p>
                  <p className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
                    <TemplateBadge templateKind={event.templateKind} className="text-sm leading-none" />
                    {event.title}
                  </p>
                  {event.location && <p className="text-xs text-slate-400 truncate mt-0.5">{event.location}</p>}
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    <CardPills event={event} />
                  </div>
                </div>
                {onShare && (
                  <button
                    onClick={e => { e.preventDefault(); e.stopPropagation(); onShare(event.id, event.title); }}
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-indigo-500 self-start mt-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                    </svg>
                  </button>
                )}
              </EventLink>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Location-grouped view ────────────────────────────────────────────────────

function LocationGrouped({ events, onShare }: { events: CarouselEvent[]; onShare?: ShareHandler }) {
  const byLocation: Record<string, CarouselEvent[]> = {};
  for (const e of events) {
    const loc = e.location || 'Unknown location';
    if (!byLocation[loc]) byLocation[loc] = [];
    byLocation[loc].push(e);
  }
  for (const loc of Object.keys(byLocation)) {
    byLocation[loc].sort((a, b) => {
      const da = a.eventDate ? parseEventDate(a.eventDate).getTime() : 0;
      const db = b.eventDate ? parseEventDate(b.eventDate).getTime() : 0;
      return db - da;
    });
  }
  return (
    <div className="space-y-10">
      {Object.entries(byLocation).sort(([a], [b]) => {
        if (a === 'Unknown location') return 1;
        if (b === 'Unknown location') return -1;
        return a.localeCompare(b);
      }).map(([loc, evts]) => (
        <div key={loc}>
          <SectionHeader label={loc} firstEventId={evts[0].id} mode="location" />
          <PolaroidGrid events={evts} onShare={onShare} />
        </div>
      ))}
    </div>
  );
}

// ─── Type-grouped view ────────────────────────────────────────────────────────
// Entirely client-side, same shape as LocationGrouped — templateKind is already
// present on every event GET /events returns, no backend query change needed.

function TypeGrouped({ events, onShare }: { events: CarouselEvent[]; onShare?: ShareHandler }) {
  const byKind: Record<string, CarouselEvent[]> = {};
  for (const e of events) {
    const kind = getEventTemplate(e.templateKind).kind;
    if (!byKind[kind]) byKind[kind] = [];
    byKind[kind].push(e);
  }
  for (const kind of Object.keys(byKind)) {
    byKind[kind].sort((a, b) => {
      const da = a.eventDate ? parseEventDate(a.eventDate).getTime() : 0;
      const db = b.eventDate ? parseEventDate(b.eventDate).getTime() : 0;
      return db - da;
    });
  }
  // Only kinds with at least one event get a section — never show empty groups.
  const orderedKinds = EVENT_TEMPLATES.map(t => t.kind).filter(kind => byKind[kind]?.length > 0);

  return (
    <div className="space-y-10">
      {orderedKinds.map(kind => {
        const config = getEventTemplate(kind);
        const evts = byKind[kind];
        return (
          <div key={kind}>
            <SectionHeader label={`${config.icon} ${config.label}`} firstEventId={evts[0].id} mode="type" />
            <PolaroidGrid events={evts} onShare={onShare} />
          </div>
        );
      })}
    </div>
  );
}
